import { HttpErrorResponse } from '@angular/common/http';
import { Component, forwardRef, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { IMsgProviderService, MSG_PROVIDER_SERVICE } from 'src/app/lib/mail/interfaces';
import { NextcloudFile } from '../../../classes';
import { FileService } from '../../../services/file.service';
import { FileViewModes } from '../../dialog/select-file-view-dialog/select-file-view-dialog.component';

@Component({
  selector: 'app-nextcloud-folder-explorer',
  templateUrl: './nextcloud-folder-explorer.component.html',
  styleUrls: ['./nextcloud-folder-explorer.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NextcloudFolderExplorerComponent),
      multi: true
    }
  ]
})
export class NextcloudFolderExplorerComponent implements ControlValueAccessor, OnDestroy, OnInit {

  private _folder: BehaviorSubject<NextcloudFile[]> = new BehaviorSubject([]);
  folder$ = this._folder.asObservable();

  error = null;

  private _loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loading$ = this._loading.asObservable();

  rootFolder: NextcloudFile;
  rootFolderName: string;

  formGroup: FormGroup;

  @Input() view = FileViewModes.List;

  private _subscriptions: Subscription[] = [];

  constructor(
    @Optional() @Inject(MSG_PROVIDER_SERVICE) private _msgProviderService: IMsgProviderService,
    private _formBuilder: FormBuilder,
    private _fileService: FileService
  ) {
    this.formGroup = this._formBuilder.group({
      location: null
    });
  }

  previewFile(event: Event, file: NextcloudFile) {
    if (event) event.stopPropagation();
    this._fileService.getRelativePath(file.href).subscribe((path) => {
      if (path.isFile) this._fileService.previewFile(file);
    });
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    this._subscriptions.push(...[
      this.locationControl.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((value) => {
          this.rootFolder = null;
          this.rootFolderName = null;
          this._folder.next([]);
          if (!value) return;
          this._loading.next(true);
          this._fileService.getFolderByPath(value).subscribe(
              (result: NextcloudFile[]) => {
                this._loading.next(false);
                this.error = null;
                this._folder.next(result.filter(x => !x.isItself));
                this.rootFolder = result.find(x => x.isItself);
                if (this.rootFolder && this.rootFolder.href) this.rootFolderName = decodeURIComponent(FileService.getFileName(this.rootFolder.href));
                else this.rootFolderName = null;
                if(this._msgProviderService) this._msgProviderService.setMsgFiles(result.filter(x => {
                  var array = x.href.split('.');
                  return array.length >= 2 && array[array.length - 1] === 'msg';
                }).map(x => {
                  return { 'blobProvider': () => this._fileService.getFileByPath(x.href), 'fileName': FileService.getFileName(x.href) }
                }))
              },
              (error: HttpErrorResponse) => {
                this._folder.next([]);

                switch (error.status) {
                  case 401:
                    this.error = 'Ihr Login an der Nextcloud ist fehlgeschlagen';
                    break;
                  case 404:
                    this.error = 'Dieser Ordner existiert nicht auf der Nextcloud';
                    break;
                }

                this.rootFolder = null;
                this.rootFolderName = null;

                this._loading.next(false);
              }
            );
        })
    ]);
  }

  public onTouched: () => void = () => { };

  registerOnChange(fn: any): void {
    this
      ._subscriptions
      .push(
        this
          .locationControl
          .valueChanges
          .subscribe(fn)
      );
  }

  registerOnTouched = (fn: any) => this.onTouched = fn;

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  writeValue = (val: any) => this.locationControl.setValue(val);

  get locationControl(): AbstractControl {
    return this.formGroup.controls['location'];
  }

}
